import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { ApiError } from "../api/client";
import { useSession } from "../session";
import { GalleryCard } from "../ui/GalleryCard";
import { ErrorNote } from "../ui/States";
import { useLoad } from "../ui/useLoad";
import { finalizeForm, type VideoFormValue } from "./uploadQueue";

export function VideoFields({
  value,
  onChange,
  onValidChange,
}: {
  value: VideoFormValue;
  onChange: (value: VideoFormValue) => void;
  onValidChange?: (valid: boolean) => void;
}) {
  const { client } = useSession();
  const location = useLocation();
  const [newSeries, setNewSeries] = useState("");
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [error, setError] = useState<unknown>();
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const series = useLoad(
    () => client.call("videos.seriesList", { query: { mine: true, limit: 100 } }),
    `mine-series:${value.seriesId ?? ""}`,
  );
  const tag = value.tagText ?? "";
  const gidText = value.gidText ?? (value.gids ?? []).join(", ");
  const suggestions = useLoad(
    async () =>
      tag.trim()
        ? (await client.call("catalog.suggestTags", { query: { q: tag.trim(), limit: 8 } })).items
        : [],
    tag,
  );
  let normalized: ReturnType<typeof finalizeForm> | undefined;
  let inputError = "";
  try {
    normalized = finalizeForm(value);
  } catch (e) {
    inputError = e instanceof Error ? e.message : "입력을 확인하세요.";
  }
  const validationKey = JSON.stringify(
    normalized?.tags ?? [
      ...new Set([...(value.tags ?? []), ...(tag.trim() ? [tag.trim().toLowerCase()] : [])]),
    ],
  );
  const [validation, setValidation] = useState<{ key: string; blocked: string[]; failed: boolean } | null>(
    null,
  );
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      client.call("videos.validateTags", { body: { tags: JSON.parse(validationKey) as string[] } }).then(
        (v) => live && setValidation({ key: validationKey, blocked: v.blockedTags, failed: false }),
        () => live && setValidation({ key: validationKey, blocked: [], failed: true }),
      );
    }, 150);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [client, validationKey]);
  const valid =
    !inputError && validation?.key === validationKey && !validation.failed && !validation.blocked.length;
  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);
  const gidsKey = JSON.stringify(normalized?.gids ?? []);
  const galleries = useLoad(
    () =>
      Promise.all(
        (JSON.parse(gidsKey) as number[]).map((gid) => client.call("catalog.get", { params: { gid } })),
      ),
    gidsKey,
  );
  const add = (name: string) => {
    const tags = [...new Set([...(value.tags ?? []), name.trim().toLowerCase()])];
    if (tags.length > 30) {
      setError(new Error("태그는 최대 30개입니다."));
      return;
    }
    onChange({ ...value, tags, tagText: "" });
    setError(undefined);
  };
  return (
    <>
      <label>
        제목
        <input
          required
          maxLength={120}
          value={value.title ?? ""}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
      </label>
      <label>
        설명
        <textarea
          maxLength={2000}
          value={value.description ?? ""}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
        />
      </label>
      <div>
        <label>
          태그
          <input
            value={tag}
            onChange={(e) => onChange({ ...value, tagText: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                e.preventDefault();
                if (tag.trim()) add(tag);
              }
            }}
          />
        </label>
        <small>Enter로 추가합니다. 입력 중인 태그도 업로드를 시작할 때 확인합니다.</small>
        <div className="video-tags-list">
          {value.tags?.map((t) => (
            <button
              type="button"
              key={t}
              aria-label={`${t} 태그 제거`}
              onClick={() => onChange({ ...value, tags: value.tags?.filter((x) => x !== t) })}
            >
              {t} ×
            </button>
          ))}
        </div>
        {suggestions.data?.map((t) => (
          <button type="button" key={t.tag} onClick={() => add(t.tag)}>
            {t.tag}
            {t.policy !== "none" && ` · ${t.policy === "block" ? "차단: 올릴 수 없습니다" : "경고"}`}
          </button>
        ))}
        {validation?.key === validationKey && validation.blocked.length > 0 && (
          <p role="alert">차단된 태그는 올릴 수 없습니다: {validation.blocked.join(", ")}</p>
        )}
        {validation?.key === validationKey && validation.failed && (
          <p role="alert">
            태그 정책을 확인하지 못했습니다. 시작할 때 다시 확인하며 실패하면 전송하지 않습니다.
          </p>
        )}
      </div>
      <label>
        작품
        <select
          value={value.seriesId ?? ""}
          onChange={(e) =>
            onChange({
              ...value,
              seriesId: e.target.value || null,
              episodeNo: e.target.value ? (value.episodeNo ?? 1) : null,
              episodeText: e.target.value ? (value.episodeText ?? String(value.episodeNo ?? 1)) : undefined,
            })
          }
        >
          <option value="">없음 · 독립 영상</option>
          {series.data?.items.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
      </label>
      {series.status === "error" && <ErrorNote error={series.error} onRetry={series.reload} />}
      <details className="upload-optional">
        <summary>새 작품 만들기</summary>
        <div className="video-actions">
          <label>
            새 작품 이름
            <input maxLength={120} value={newSeries} onChange={(e) => setNewSeries(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn"
            disabled={creating || !newSeries.trim()}
            onClick={async () => {
              if (creatingRef.current) return;
              creatingRef.current = true;
              setCreating(true);
              try {
                const s = await client.call("videos.seriesCreate", { body: { title: newSeries.trim() } });
                if (mounted.current) {
                  series.reload();
                  latest.current.onChange({
                    ...latest.current.value,
                    seriesId: s.id,
                    episodeNo: 1,
                    episodeText: "1",
                  });
                  setNewSeries("");
                }
              } catch (e) {
                setError(e);
              } finally {
                creatingRef.current = false;
                setCreating(false);
              }
            }}
          >
            {creating ? "만드는 중" : "작품 만들기"}
          </button>
        </div>
        <small>작품 만들기를 누르면 작품이 서버에 저장됩니다.</small>
      </details>
      {value.seriesId && (
        <label>
          회차 번호 (0은 예고편)
          <input
            type="number"
            min={0}
            max={999}
            step={1}
            value={value.episodeText ?? value.episodeNo ?? 1}
            onChange={(e) => onChange({ ...value, episodeText: e.target.value })}
          />
        </label>
      )}
      <details className="upload-optional" open={!!(value.gidText || value.gids?.length)}>
        <summary>원작 연결 (선택)</summary>
        <label>
          원작 동인지 GID (최대 5개, 쉼표로 구분)
          <input value={gidText} onChange={(e) => onChange({ ...value, gidText: e.target.value })} />
        </label>
        <div className="video-linked-preview">
          {galleries.data?.map((g) => (
            <GalleryCard
              key={g.gid}
              card={g}
              to={`/g/${g.gid}`}
              state={{ inApp: true, background: location }}
            />
          ))}
        </div>
        {galleries.status === "error" && <ErrorNote error={galleries.error} />}
      </details>
      {inputError && <p role="alert">{inputError}</p>}
      {error !== undefined && (
        <ErrorNote
          error={error}
          message={error instanceof Error && !(error instanceof ApiError) ? error.message : undefined}
        />
      )}
    </>
  );
}
